// src/services/meeting.service.ts
import api from './api.ts';

export interface Meeting {
  id: string;
  meetingCode: string;
  title: string;
  description?: string;
  ownerId: string;
  password?: string;
  scheduledAt?: string;
  duration: number;
  meetingFormat: 'ONLINE' | 'IN_PERSON';
  meetingType: 'VIDEO' | 'AUDIO' | 'SCREEN' | 'IN_PERSON';
  location?: string;
  address?: string;
  building?: string;
  room?: string;
  isRecurring: boolean;
  recurrenceType?: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  isInstant: boolean;
  status: 'SCHEDULED' | 'LIVE' | 'ENDED' | 'CANCELLED';
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  endedAt?: string;
  owner?: {
    id: string;
    name: string;
    email: string;
  };
  participants?: Array<{
    id: string;
    email: string;
    name?: string;
    role: string;
  }>;
  topics?: Array<{
    id: string;
    title: string;
    duration: number;
    type: string;
    order: number;
  }>;
}

export interface CreateMeetingData {
  title: string;
  description?: string;
  scheduledAt?: string;
  duration?: number;
  meetingFormat?: 'ONLINE' | 'IN_PERSON';
  meetingType?: 'VIDEO' | 'AUDIO' | 'SCREEN' | 'IN_PERSON';
  location?: string;
  address?: string;
  building?: string;
  room?: string;
  isRecurring?: boolean;
  recurrenceType?: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  participants?: Array<{ email: string; name?: string }>;
  password?: string;
  isInstant?: boolean;
  topics?: Array<{ title: string; duration: number; type: string; order: number }>;
}

export const meetingService = {
  async getMeetings(): Promise<Meeting[]> {
    const response = await api.get('/api/meetings');
    return response.data.data || response.data || [];
  },

  async getMeeting(id: string): Promise<Meeting> {
    const response = await api.get(`/api/meetings/${id}`);
    return response.data.data || response.data;
  },

  async getMeetingByCode(code: string): Promise<Meeting> {
    try {
      const response = await api.get(`/api/meetings/code/${code}`);
      return response.data.data || response.data;
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        // Anlık / yalnızca socket odası — DB kaydı olmayabilir; ikinci 404 istemez
        const notFound = new Error('MEETING_NOT_IN_DB');
        (notFound as Error & { code?: string }).code = 'MEETING_NOT_IN_DB';
        throw notFound;
      }
      throw error;
    }
  },

  async createMeeting(data: CreateMeetingData): Promise<Meeting> {
    const response = await api.post('/api/meetings', data);
    return response.data.data || response.data;
  },

  async updateMeeting(id: string, data: Partial<CreateMeetingData>): Promise<Meeting> {
    const response = await api.put(`/api/meetings/${id}`, data);
    return response.data.data || response.data;
  },

  async deleteMeeting(id: string): Promise<void> {
    await api.delete(`/api/meetings/${id}`);
  },

  async startMeeting(id: string): Promise<Meeting> {
    const response = await api.post(`/api/meetings/${id}/start`);
    return response.data.data || response.data;
  },

  async endMeeting(id: string): Promise<Meeting> {
    const response = await api.post(`/api/meetings/${id}/end`);
    return response.data.data || response.data;
  },

  async joinMeeting(id: string, password?: string): Promise<Meeting> {
    const response = await api.post(`/api/meetings/${id}/join`, { password });
    return response.data.data || response.data;
  },

  async leaveMeeting(id: string): Promise<void> {
    await api.post(`/api/meetings/${id}/leave`);
  },

  async expireLiveMeeting(id: string): Promise<void> {
    await api.post(`/api/meetings/${id}/expire-live`);
  },
};